import {
  createUnifiedTheme,
  defaultComponentThemes,
  genPageTheme,
  pageTheme,
  palettes,
  UnifiedTheme,
  UnifiedThemeOptions,
} from '@backstage/theme';
import themeConstants from './constants';

const { text, neutral, navigation, link } = themeConstants.palette;

const whitePageTheme = genPageTheme({
  colors: [neutral.lighter],
  shape: 'none',
  options: { fontColor: text.primary },
});

const baselineDefaults = (theme: unknown): Record<string, any> => {
  const overrides: unknown =
    defaultComponentThemes?.MuiCssBaseline?.styleOverrides;
  return typeof overrides === 'function' ? overrides(theme) : {};
};

export class LightTheme {
  public static readonly theme = (): UnifiedTheme => this.createTheme();

  private static createTheme = (): UnifiedTheme =>
    createUnifiedTheme({
      palette: {
        ...palettes.light,
        primary: { main: link.main },
        text: {
          primary: text.primary,
          secondary: text.secondary,
          disabled: text.disabled,
        },
        background: {
          default: palettes.light.background.default,
          paper: neutral.lighter,
        },
        border: neutral.main,
        textSubtle: text.light,
        textContrast: text.primary,
        link: link.main,
        linkHover: link.hover,
        navigation: {
          ...palettes.light.navigation,
          background: navigation.background,
          color: navigation.color,
          selectedColor: navigation.selectedColor,
          indicator: navigation.indicator,
          navItem: { hoverBackground: navigation.submenu },
          submenu: { background: navigation.submenu },
        },
        tabbar: { indicator: link.main },
      },
      pageTheme: Object.fromEntries(
        Object.keys(pageTheme).map(key => [key, whitePageTheme]),
      ),
      defaultPageTheme: 'home',
      fontFamily: themeConstants.typography.fontFamily,
      components: this.customComponentStyles(),
    });

  private static customComponentStyles =
    (): UnifiedThemeOptions['components'] => ({
      MuiCssBaseline: {
        styleOverrides: (theme: any) => {
          const base = baselineDefaults(theme);
          return {
            ...base,
            body: {
              ...base.body,
              '--bui-fg-primary': text.primary,
              '--bui-fg-secondary': text.light,
            },
          };
        },
      },
      BackstageHeader: {
        styleOverrides: {
          header: () => ({
            backgroundImage: 'none',
            backgroundColor: neutral.lighter,
            boxShadow: 'none',
            borderBottom: `1px solid ${neutral.main}`,
          }),
          breadcrumb: {
            '& a': {
              color: link.main,
              '&:hover': {
                color: link.hover,
              },
            },
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: link.main,
          },
        },
      },
      BackstageSidebar: {
        styleOverrides: {
          drawer: {
            '& .MuiSvgIcon-root': {
              color: 'inherit',
            },
          },
        },
      },
      MuiButton: {
        ...defaultComponentThemes?.MuiButton,
        styleOverrides: {
          ...defaultComponentThemes?.MuiButton?.styleOverrides,
          contained: {
            '& .MuiSvgIcon-root': {
              color: 'inherit',
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          icon: {
            '& .MuiSvgIcon-root': {
              color: 'inherit',
            },
          },
        },
      },
      BackstageTableHeader: {
        styleOverrides: {
          header: {
            color: text.primary,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          tag: {
            backgroundColor: link.background,
            color: link.main,
            border: `1px solid ${link.main}`,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          colorDefault: {
            backgroundColor: link.background,
            border: `1px solid ${link.main}`,
          },
        },
      },
      MuiChip: {
        ...defaultComponentThemes?.MuiChip,
        styleOverrides: {
          ...defaultComponentThemes?.MuiChip?.styleOverrides,
          root: ({ theme }) => ({
            marginRight: theme.spacing(1),
            marginBottom: theme.spacing(1),
            border: `1px solid ${link.main}`,
            color: link.main,
            backgroundColor: link.background,
          }),
          outlined: {
            borderColor: link.main,
            color: link.main,
            backgroundColor: link.background,
          },
          deleteIcon: {
            color: link.main,
            '&:hover': {
              color: link.hover,
            },
          },
        },
      },
    });
}
